import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Calendar, Check, X } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import {
  computeDeadlineCountdown,
  formatDeadlineLong,
  isoToLocalInput,
  localInputToIso,
} from '@/lib/student-objectives/deadline-format';
import { updateObjective } from '@/lib/student-objectives/student-objectives';
import { supabase } from '@/lib/supabase';
import type { AppStudentObjective } from '@/lib/student-objectives/objective-types';

interface Props {
  objective: AppStudentObjective;
}

export function DeadlineEditor({ objective }: Props) {
  const queryClient = useQueryClient();
  const isEpreuve = objective.template.contentType === 'epreuves';
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(isoToLocalInput(objective.deadline));
  const [saving, setSaving] = useState(false);

  const countdown = computeDeadlineCountdown(objective.deadline);

  async function save(next: string | null) {
    setSaving(true);
    try {
      await updateObjective(supabase, objective.id, { deadline: next });
      await queryClient.invalidateQueries({ queryKey: ['student-objectives'] });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  if (editing) {
    return (
      <div className="border-ink-100 flex flex-col gap-2 rounded-2xl border bg-white/80 p-3 backdrop-blur-sm sm:flex-row sm:items-center">
        <Calendar className="text-ink-500 hidden h-5 w-5 sm:block" aria-hidden="true" />
        <input
          type="datetime-local"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="border-ink-200 text-ink-900 focus:ring-primary-400 min-h-11 flex-1 rounded-xl border px-3 py-2 font-sans text-[14px] focus:outline-none focus:ring-2"
        />
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => save(localInputToIso(value))}
            disabled={saving}
            className="bg-primary-600 hover:bg-primary-700 inline-flex h-11 items-center gap-1 rounded-xl px-3 font-sans text-[13px] font-semibold text-white active:scale-95 disabled:opacity-50"
          >
            <Check className="h-4 w-4" /> Enregistrer
          </button>
          <button
            type="button"
            onClick={() => {
              setValue(isoToLocalInput(objective.deadline));
              setEditing(false);
            }}
            className="border-ink-200 text-ink-700 hover:bg-ink-50 inline-flex h-11 items-center gap-1 rounded-xl border bg-white px-3 font-sans text-[13px] active:scale-95"
          >
            <X className="h-4 w-4" /> Annuler
          </button>
          {objective.deadline && (
            <button
              type="button"
              onClick={() => save(null)}
              disabled={saving}
              className="text-ink-500 hover:text-ink-700 inline-flex h-11 items-center px-2 font-sans text-[12px] underline"
            >
              Retirer
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => {
        setValue(isoToLocalInput(objective.deadline));
        setEditing(true);
      }}
      className="border-ink-100 hover:border-primary-300 hover:bg-primary-50/40 flex w-full items-center gap-3 rounded-2xl border bg-white/70 p-3 text-left backdrop-blur-sm transition-colors active:scale-[0.99]"
    >
      <Calendar
        className={cn('h-5 w-5 shrink-0', countdown?.urgent ? 'text-accent-600' : 'text-ink-500')}
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">
        <p className="font-display text-ink-500 text-[11px] font-semibold uppercase tracking-wider">
          Date butoir{!isEpreuve && !objective.deadline && ' (optionnel)'}
        </p>
        {objective.deadline ? (
          <p
            className={cn(
              'font-display mt-0.5 text-[15px] font-semibold',
              countdown?.expired || countdown?.urgent ? 'text-accent-600' : 'text-ink-900',
            )}
          >
            {formatDeadlineLong(objective.deadline)}
          </p>
        ) : (
          <p className="text-ink-500 mt-0.5 font-sans text-[13px]">
            Aucune deadline — touche pour en définir une
          </p>
        )}
      </div>
      <span className="border-ink-200 text-ink-500 inline-flex h-9 items-center justify-center rounded-full border bg-white px-3 font-sans text-[12px] font-semibold">
        Modifier
      </span>
    </button>
  );
}
